<script lang="ts" setup>
import {
  COACH_RESET_CURRENT_SESSION_MUTATION,
  COACH_RESET_USER_MUTATION,
} from '~/graphql/documents/coach';
import { UNIFIED_RESET_ALL } from '~/graphql/documents/unified';

const client = useUrqlClient();
const { show: showToast } = useToast();
const isLoading = ref(false);

// Every one of these takes state away, so the page it was fired from is describing a session that
// no longer exists — reload rather than try to reconcile it.
const resetThen = (message: string, action: () => Promise<void>) => async () => {
  if (isLoading.value) {
    return;
  }
  isLoading.value = true;
  try {
    await action();
    showToast({ variant: 'success', message });
    window.location.reload();
  }
  catch (err) {
    isLoading.value = false;
    showToast({ variant: 'error', message: err instanceof Error ? err.message : 'Reset failed' });
  }
};

const resetCurrentSession = resetThen('Current session reset', async () => {
  const result = await client.mutation(COACH_RESET_CURRENT_SESSION_MUTATION, {});
  if (result.error) {
    throw result.error;
  }
});

const resetCoachUser = resetThen('Coach user reset', async () => {
  const result = await client.mutation(COACH_RESET_USER_MUTATION, {});
  if (result.error) {
    throw result.error;
  }
});

const resetAll = resetThen('Reset — back to signup', async () => {
  const result = await client.mutation(UNIFIED_RESET_ALL, {});
  if (result.error) {
    throw result.error;
  }
});
</script>

<template>
  <div class="px-3 py-1">
    <div class="grid grid-cols-2 gap-1.5">
      <button
        class="rounded-md px-2 py-0.5 text-xs font-medium text-white bg-warning hover:bg-warning/90 disabled:opacity-50"
        :disabled="isLoading"
        @click="resetCurrentSession"
      >
        Reset current session
      </button>
      <button
        class="rounded-md px-2 py-0.5 text-xs font-medium text-primary-foreground bg-primary hover:brightness-90 disabled:opacity-50"
        :disabled="isLoading"
        @click="resetCoachUser"
      >
        Reset coach user
      </button>
      <button
        class="col-span-2 rounded-md px-2 py-0.5 text-xs font-medium text-white bg-destructive hover:bg-destructive/90 disabled:opacity-50"
        :disabled="isLoading"
        @click="resetAll"
      >
        Reset all (back to signup)
      </button>
    </div>
  </div>
</template>
